<template>
    <!-- Permission coach (Android-style): shown in the Ironbook tab BEFORE Chrome's share picker, so the
         audio switch is pointed out before people click straight through a dialog they've seen before. -->
    <Teleport to="body">
        <div class="share-coach-backdrop" @click.self="$emit('close')">
            <div class="share-coach" role="dialog" aria-modal="true" aria-labelledby="share-coach-title" aria-describedby="share-coach-text" @keydown.esc.prevent="$emit('close')" @keydown.tab="trapTab">
                <button v-if="mode === 'help'" type="button" class="share-coach-x" aria-label="Close" @click="$emit('close')">×</button>
                <h2 id="share-coach-title">Record everyone on the call</h2>
                <p id="share-coach-text">Chrome will ask what to share. Turn on <strong>Share with system audio</strong> or people on the call won’t be recorded.</p>
                <AiShareGuide class="share-coach-guide" />
                <div class="share-coach-actions">
                    <button ref="primary" type="button" class="share-coach-primary" @click="confirm">{{ mode === 'start' ? 'Got it — open the picker' : 'Got it' }}</button>
                    <button v-if="mode === 'start'" type="button" class="share-coach-cancel" @click="$emit('close')">Cancel</button>
                </div>
                <label v-if="mode === 'start'" class="share-coach-optout"><input v-model="dontShow" type="checkbox" />Don’t show this again</label>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import AIShareGuide from './AIShareGuide.vue';
// mode 'start': opened by Start; "Got it — open the picker" emits confirm({ dontShow }) and the parent opens
// the picker from that same click. mode 'help': opened from "How do I include call audio?"; starts nothing.
export default defineComponent({
    name: 'AIShareCoach',
    components: { AiShareGuide: AIShareGuide },
    props: { mode: { type: String, default: 'start' } },
    emits: ['confirm', 'close'],
    data() { return { dontShow: false, returnFocus: null as HTMLElement | null }; },
    mounted() {
        this.returnFocus = document.activeElement as HTMLElement | null;
        (this.$refs.primary as HTMLElement)?.focus();
    },
    beforeUnmount() {
        if (this.returnFocus && document.contains(this.returnFocus)) this.returnFocus.focus();
    },
    methods: {
        // Synchronous on purpose: the parent must call getDisplayMedia inside this click.
        confirm() { this.$emit('confirm', { dontShow: this.dontShow }); },
        trapTab(e: KeyboardEvent) {
            const items = Array.from((e.currentTarget as HTMLElement).querySelectorAll('button, input')) as HTMLElement[];
            if (!items.length) return;
            const first = items[0], last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
    }
});
</script>

<style scoped>
.share-coach-backdrop { position: fixed; inset: 0; z-index: 10000001; /* above the app's modals (up to 10000000) and the phone nav */ background: rgba(30, 20, 12, .55); display: flex; align-items: flex-start; justify-content: center; overflow-y: auto; padding: 40px 16px; }
.share-coach { position: relative; width: 100%; max-width: 620px; margin: auto; background: #fff; border-radius: 14px; box-shadow: 0 24px 60px rgba(30, 20, 12, .35); padding: 26px 28px 22px; color: #34261c; }
.share-coach h2 { margin: 0 0 6px; font-size: 22px; line-height: 1.25; }
.share-coach p { margin: 0 0 16px; font-size: 15px; line-height: 1.5; }
.share-coach-guide :deep(svg) { max-width: 560px; margin: 0 auto; filter: drop-shadow(0 2px 8px rgba(30, 20, 12, .12)); }
.share-coach-guide :deep(.share-steps) { font-size: 15px; gap: 9px; margin-top: 16px; }
.share-coach-guide :deep(.share-steps b) { flex-basis: 24px; height: 24px; font-size: 13px; }
.share-coach-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-top: 20px; }
.share-coach-primary { min-height: 44px; padding: 0 22px; border: 0; border-radius: 9px; background: #c2410c; color: #fff; font: inherit; font-size: 15px; font-weight: 700; cursor: pointer; }
.share-coach-primary:hover { background: #a8380a; }
.share-coach-cancel { min-height: 44px; padding: 0 12px; border: 0; background: none; color: #6d5848; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; text-decoration: underline; }
.share-coach-primary:focus-visible, .share-coach-cancel:focus-visible, .share-coach-x:focus-visible, .share-coach-optout input:focus-visible { outline: 2px solid #ad4c0c; outline-offset: 2px; }
.share-coach-optout { display: inline-flex; align-items: center; gap: 7px; margin-top: 14px; font-size: 13px; color: #6d5848; cursor: pointer; }
.share-coach-optout input { width: 15px; height: 15px; margin: 0; accent-color: #c2410c; }
.share-coach-x { position: absolute; top: 10px; right: 12px; width: 36px; height: 36px; border: 0; background: none; font-size: 26px; line-height: 1; color: #6d5848; cursor: pointer; border-radius: 8px; }
.share-coach-x:hover { background: #f5ede6; }
@media (max-width: 520px) {
    .share-coach-backdrop { padding: 16px; }
    .share-coach { padding: 20px 16px 18px; }
    .share-coach h2 { font-size: 19px; padding-right: 28px; }
    .share-coach-primary { width: 100%; }
}
</style>
